(() => {
  class AiProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.desktopThumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
      this.mainImages = [...this.querySelectorAll('[data-gallery-image]')];
      this.desktopMain = this.mainImages[0]?.parentElement;
      this.mobileTrack = this.querySelector('[data-gallery-mobile-track]');
      this.mobileThumbnails = [...this.querySelectorAll('[data-gallery-mobile-thumbnail]')];
      this.mobileThumbnailsContainer = this.querySelector('[data-gallery-mobile-thumbnails]');
      this.mobileThumbnailsTrack = this.querySelector('[data-gallery-mobile-thumbnails-track]');
      this.mobileSlides = [...this.querySelectorAll('[data-gallery-mobile-slide]')];
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
      this.lightboxClose = this.querySelector('[data-gallery-lightbox-close]');
      this.lightboxPrev = this.querySelector('[data-gallery-lightbox-prev]');
      this.lightboxNext = this.querySelector('[data-gallery-lightbox-next]');
      this.lightboxCurrent = this.querySelector('[data-gallery-lightbox-current]');
      this.lightboxContent = this.querySelector('[data-gallery-lightbox-content]');

      this.currentSlide = 0;
      this.currentDesktopSlide = 0;
      this.currentLightboxIndex = 0;
      this.isProgrammaticScroll = false;
      this.scrollResetTimer = null;

      // Build lightbox media data from ALL desktop media slides (images AND videos)
      this.mediaData = this.mainImages.map((wrapper) => {
        const video = wrapper.querySelector('video');
        if (video) {
          const source = video.querySelector('source');
          return {
            type: 'video',
            src: video.currentSrc || source?.src || video.getAttribute('src') || '',
            poster: video.poster || '',
            alt: '',
          };
        }
        const img = wrapper.querySelector('img');
        return { type: 'image', src: img?.src || '', alt: img?.alt || '' };
      });

      this.syncLightboxTotal();

      this.isDragging = false;
      this.thumbnailIsDragging = false;
      this.suppressThumbnailClick = false;
      this.bindEvents();

      // Autoplay the initial slide if it happens to be a video
      requestAnimationFrame(() => this.playActiveVideo());
    }

    syncLightboxTotal() {
      if (!this.lightboxCurrent) return;
      const wrapper = this.lightboxCurrent.parentElement;
      if (!wrapper) return;
      const total = wrapper.querySelector('[data-gallery-lightbox-total]');
      if (total) {
        total.textContent = this.mediaData.length;
      } else {
        // Fallback for older Liquid without the data attribute
        const spans = wrapper.querySelectorAll('span');
        if (spans.length >= 2) {
          spans[spans.length - 1].textContent = this.mediaData.length;
        }
      }
    }

    bindEvents() {
      /* ---------- Desktop thumbnails ---------- */
      this.desktopThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', () => {
          if (this.thumbnailIsDragging) return;
          const index = Number(thumbnail.dataset.index);
          this.currentDesktopSlide = index;
          this.scrollToImage(index);
          this.setActiveThumbnail(index);
          this.playActiveVideo();
        });
      });

      /* ---------- Desktop slide click → lightbox ---------- */
      this.mainImages.forEach((image, i) => {
        image.addEventListener('click', (event) => {
          if (this.isDragging) return;
          // Don't open the lightbox when the click is on the video itself
          if (event.target.closest('video')) return;
          this.openLightbox(i);
        });
      });

      /* ---------- Desktop scroll → active thumb + video autoplay ---------- */
      if (this.desktopMain) {
        let scrollTimeout;
        this.desktopMain.addEventListener('scroll', () => {
          if (this.isProgrammaticScroll) return;
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            const width = this.desktopMain.clientWidth;
            if (!width) return;
            const idx = Math.round(this.desktopMain.scrollLeft / width);
            if (idx >= 0 && idx < this.mainImages.length && idx !== this.currentDesktopSlide) {
              this.currentDesktopSlide = idx;
              this.setActiveThumbnail(idx);
              this.playActiveVideo();
            }
          }, 100);
        }, { passive: true });
      }

      /* ---------- Mobile thumbnails ---------- */
      this.mobileThumbnails.forEach((thumbnail) => {
        thumbnail.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.suppressThumbnailClick) {
            this.suppressThumbnailClick = false;
            return;
          }
          this.goToSlide(Number(thumbnail.dataset.mobileIndex));
        });
      });

      /* ---------- Mobile slide click → lightbox ---------- */
      this.mobileSlides.forEach((slide, i) => {
        slide.addEventListener('click', (event) => {
          if (this.isDragging) return;
          if (event.target.closest('video')) return;
          this.openLightbox(i);
        });
      });

      /* ---------- Lightbox ---------- */
      this.lightboxClose?.addEventListener('click', () => this.closeLightbox());
      this.lightboxPrev?.addEventListener('click', () => this.navigateLightbox(-1));
      this.lightboxNext?.addEventListener('click', () => this.navigateLightbox(1));

      this.lightboxImage?.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!this.lightboxContent || !this.lightboxImage) return;
        // No zoom if the image is hidden (a video is showing)
        if (this.lightboxImage.style.display === 'none') return;

        if (this.lightboxContent.classList.contains('zoomed')) {
          this.resetLightboxZoom();
          return;
        }

        const bounds = this.lightboxImage.getBoundingClientRect();
        const fx = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
        const fy = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height));
        this.lightboxContent.classList.add('zoomed');

        requestAnimationFrame(() => {
          const tx = (this.lightboxImage.scrollWidth * fx) - (this.lightboxContent.clientWidth / 2);
          const ty = (this.lightboxImage.scrollHeight * fy) - (this.lightboxContent.clientHeight / 2);
          this.lightboxContent.scrollTo({
            left: Math.max(0, tx),
            top: Math.max(0, ty),
            behavior: 'auto',
          });
        });
      });

      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      });

      document.addEventListener('keydown', (event) => {
        if (!this.lightbox?.classList.contains('active')) return;
        if (event.key === 'Escape') this.closeLightbox();
        if (event.key === 'ArrowLeft') this.navigateLightbox(-1);
        if (event.key === 'ArrowRight') this.navigateLightbox(1);
      });

      this.setupMainDrag();
      this.setupThumbnailDrag();
    }

    /* ================================================================
       VIDEO AUTOPLAY / STOP
       ================================================================ */
    playActiveVideo() {
      // Pause EVERY video in the gallery (inline + mobile)
      this.querySelectorAll('video').forEach((v) => {
        // Ignore the lightbox video; it has its own lifecycle
        if (this.lightbox && this.lightbox.contains(v)) return;
        try { v.pause(); } catch (e) {}
      });

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const slides = isMobile ? this.mobileSlides : this.mainImages;
      const idx = isMobile ? this.currentSlide : this.currentDesktopSlide;
      const slide = slides[idx];
      if (!slide) return;

      // IMPORTANT: just look for a <video> — don't rely on data-media-type
      const video = slide.querySelector('video');
      if (!video) return;

      // Force-mute so browsers always allow autoplay
      video.muted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');

      const tryPlay = () => {
        try {
          const p = video.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
        } catch (e) { /* ignore */ }
      };

      if (video.readyState >= 1) {
        try { video.currentTime = 0; } catch (e) {}
      } else {
        video.addEventListener('loadedmetadata', () => {
          try { video.currentTime = 0; } catch (e) {}
        }, { once: true });
      }
      tryPlay();
    }

    /* ================================================================
       MOBILE MAIN DRAG
       ================================================================ */
    setupMainDrag() {
      if (!this.mobileTrack) return;
      let startX = 0, currentX = 0, startTranslate = 0;

      this.mobileTrack.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // Let native video controls handle their own interactions
        if (event.target.closest('video')) return;
        this.isDragging = false;
        startX = event.clientX;
        currentX = startX;
        startTranslate = -this.currentSlide * this.mobileTrack.clientWidth;
        this.mobileTrack.classList.add('dragging');
        this.mobileTrack.setPointerCapture?.(event.pointerId);
      });

      this.mobileTrack.addEventListener('pointermove', (event) => {
        if (!this.mobileTrack.classList.contains('dragging')) return;
        currentX = event.clientX;
        const distance = currentX - startX;
        if (Math.abs(distance) > 5) this.isDragging = true;
        this.mobileTrack.style.transform = `translateX(${startTranslate + distance}px)`;
      });

      this.mobileTrack.addEventListener('pointerup', (event) => {
        if (!this.mobileTrack.classList.contains('dragging')) return;
        this.mobileTrack.classList.remove('dragging');
        this.mobileTrack.releasePointerCapture?.(event.pointerId);
        const distance = currentX - startX;
        const threshold = this.mobileTrack.clientWidth * 0.2;
        if (distance < -threshold) this.goToSlide(this.currentSlide + 1);
        else if (distance > threshold) this.goToSlide(this.currentSlide - 1);
        else this.goToSlide(this.currentSlide);
        window.setTimeout(() => { this.isDragging = false; }, 50);
      });
    }

    /* ================================================================
       MOBILE THUMBNAIL DRAG
       ================================================================ */
    setupThumbnailDrag() {
      if (!this.mobileThumbnailsTrack) return;
      let startX = 0, startTranslate = 0, currentX = 0, moved = false;

      this.mobileThumbnailsTrack.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        currentX = startX;
        moved = false;
        const t = getComputedStyle(this.mobileThumbnailsTrack).transform;
        startTranslate = t === 'none' ? 0 : new DOMMatrix(t).m41;
        this.mobileThumbnailsTrack.classList.add('dragging');
        this.mobileThumbnailsTrack.setPointerCapture?.(event.pointerId);
      });

      this.mobileThumbnailsTrack.addEventListener('pointermove', (event) => {
        if (!this.mobileThumbnailsTrack.classList.contains('dragging')) return;
        currentX = event.clientX;
        const distance = currentX - startX;
        if (Math.abs(distance) > 8) {
          moved = true;
          this.thumbnailIsDragging = true;
          this.suppressThumbnailClick = true;
          event.preventDefault();
        }
        const maxT = 0;
        const minT = Math.min(0, this.mobileThumbnailsContainer.clientWidth - this.mobileThumbnailsTrack.scrollWidth);
        const t = Math.max(minT, Math.min(maxT, startTranslate + distance));
        this.mobileThumbnailsTrack.style.transform = `translateX(${t}px)`;
      });

      this.mobileThumbnailsTrack.addEventListener('pointerup', (event) => {
        if (!this.mobileThumbnailsTrack.classList.contains('dragging')) return;
        this.mobileThumbnailsTrack.classList.remove('dragging');
        this.mobileThumbnailsTrack.releasePointerCapture?.(event.pointerId);
        this.suppressThumbnailClick = moved;
        this.thumbnailIsDragging = false;
        window.setTimeout(() => { this.suppressThumbnailClick = false; }, 50);
      });

      this.mobileThumbnailsTrack.addEventListener('pointercancel', (event) => {
        this.mobileThumbnailsTrack.classList.remove('dragging');
        this.mobileThumbnailsTrack.releasePointerCapture?.(event.pointerId);
        this.thumbnailIsDragging = false;
        this.suppressThumbnailClick = false;
      });
    }

    /* ================================================================
       DESKTOP SCROLL + ACTIVE THUMBNAIL
       ================================================================ */
    scrollToImage(index) {
      const target = this.mainImages[index];
      const galleryTrack = target?.parentElement;
      if (!target || !galleryTrack) return;
      this.isProgrammaticScroll = true;
      galleryTrack.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      clearTimeout(this.scrollResetTimer);
      this.scrollResetTimer = setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 700);
    }

    setActiveThumbnail(index) {
      this.desktopThumbnails.forEach((t, i) => t.classList.toggle('active', i === index));
    }

    /* ================================================================
       MOBILE SLIDE NAV
       ================================================================ */
    goToSlide(index) {
      if (!this.mobileTrack || !this.mobileSlides.length) return;
      this.currentSlide = Math.max(0, Math.min(index, this.mobileSlides.length - 1));
      const slideWidth = this.mobileTrack.parentElement?.clientWidth || this.mobileSlides[0].offsetWidth;
      if (!slideWidth) return;
      this.mobileTrack.style.transform = `translateX(-${this.currentSlide * slideWidth}px)`;
      this.mobileThumbnails.forEach((t, i) => t.classList.toggle('active', i === this.currentSlide));
      this.scrollThumbnailIntoView(this.currentSlide);
      this.playActiveVideo();
    }

    scrollThumbnailIntoView(index) {
      const thumbnail = this.mobileThumbnails[index];
      if (!thumbnail || !this.mobileThumbnailsTrack) return;
      const containerWidth = this.mobileThumbnailsContainer.clientWidth;
      const trackWidth = this.mobileThumbnailsTrack.scrollWidth;
      const t = getComputedStyle(this.mobileThumbnailsTrack).transform;
      const currentTranslate = t === 'none' ? 0 : new DOMMatrix(t).m41;
      const left = thumbnail.offsetLeft + currentTranslate;
      const right = left + thumbnail.offsetWidth;
      let translate = currentTranslate;
      if (right > containerWidth) translate -= right - containerWidth;
      if (left < 0) translate -= left;
      const minT = Math.min(0, containerWidth - trackWidth);
      this.mobileThumbnailsTrack.style.transform = `translateX(${Math.max(minT, Math.min(0, translate))}px)`;
    }

    /* ================================================================
       LIGHTBOX (images AND videos)
       ================================================================ */
    getOrCreateLightboxVideo() {
      let video = this.lightboxContent.querySelector('[data-gallery-lightbox-video]');
      if (!video) {
        video = document.createElement('video');
        video.setAttribute('data-gallery-lightbox-video', '');
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        video.muted = true;
        // Inline styles so we don't depend on the ai_gen_id class name
        video.style.cssText =
          'display:block;width:auto;height:auto;max-width:100vw;max-height:100vh;' +
          'object-fit:contain;background:#000;';
        this.lightboxContent.appendChild(video);
      }
      return video;
    }

    openLightbox(index) {
      if (!this.lightbox || !this.lightboxImage || !this.mediaData.length) return;

      // Pause inline videos so they don't play under the lightbox.
      // (Skip the lightbox video — it has its own lifecycle.)
      this.querySelectorAll('video').forEach((v) => {
        if (this.lightbox.contains(v)) return;
        try { v.pause(); } catch (e) {}
      });

      this.currentLightboxIndex =
        ((index % this.mediaData.length) + this.mediaData.length) % this.mediaData.length;
      this.resetLightboxZoom();
      this.updateLightboxImage();
      this.lightbox.classList.add('active');
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      const lbVideo = this.lightboxContent?.querySelector('[data-gallery-lightbox-video]');
      if (lbVideo) {
        try { lbVideo.pause(); } catch (e) {}
      }
      this.resetLightboxZoom();
      this.lightbox?.classList.remove('active');
      this.lightbox?.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    navigateLightbox(direction) {
      if (!this.mediaData.length) return;
      this.currentLightboxIndex =
        (this.currentLightboxIndex + direction + this.mediaData.length) % this.mediaData.length;
      this.resetLightboxZoom();
      this.updateLightboxImage();
    }

    resetLightboxZoom() {
      this.lightboxContent?.classList.remove('zoomed');
      if (this.lightboxContent) {
        this.lightboxContent.scrollLeft = 0;
        this.lightboxContent.scrollTop = 0;
      }
    }

    updateLightboxImage() {
      const media = this.mediaData[this.currentLightboxIndex];
      if (!media) return;

      const lbVideo = this.lightboxContent?.querySelector('[data-gallery-lightbox-video]');

      if (media.type === 'video') {
        // Hide image, show video
        this.lightboxImage.style.display = 'none';
        const video = this.getOrCreateLightboxVideo();
        video.style.display = '';
        video.poster = media.poster || '';
        if (video.src !== media.src) video.src = media.src;
        video.muted = true;
        try {
          const p = video.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {
              video.muted = true;
              video.play().catch(() => {});
            });
          }
        } catch (e) {}
      } else {
        // Pause/hide video, show image
        if (lbVideo) {
          try { lbVideo.pause(); } catch (e) {}
          lbVideo.style.display = 'none';
        }
        this.lightboxImage.style.display = '';
        this.lightboxImage.src = media.src;
        this.lightboxImage.alt = media.alt;
      }

      if (this.lightboxCurrent) this.lightboxCurrent.textContent = this.currentLightboxIndex + 1;
    }

    disconnectedCallback() {
      clearTimeout(this.scrollResetTimer);
    }
  }

  if (!customElements.get('product-gallery-product-gallery')) {
    customElements.define('product-gallery-product-gallery', AiProductGallery);
  }
})();